<template>
  <side-by-side-tab-controller v-model="menuTab" :show-side-by-side="mdAndUp">
    <template #left>
      <v-card elevation="0" class="py-3">
        <v-card-title class="pt-0"> Inbound Emails </v-card-title>
        <v-card-subtitle>
          Emails received by casemanagers@back9ins.com
        </v-card-subtitle>
        <div v-if="loadingEmails" class="px-4 pt-3">
          <v-skeleton-loader type="card" />
        </div>
        <v-list v-else-if="emails.length" class="rounded">
          <template v-for="(email, index) in emails" :key="email.id">
            <v-list-item
              data-testid="email-preview"
              :value="email.id"
              :active="activeEmailId === email.id"
              @click="viewEmail(email.id)"
            >
              <v-list-item-title>
                {{ listToSentence(email.from) }}
              </v-list-item-title>
              <v-list-item-subtitle>
                <div class="d-flex justify-space-between better-line-height">
                  <div class="text-truncate">{{ email.subject }}</div>
                  <div class="flex-shrink-0 ml-2">
                    {{ timestampFormatter(email.createdAt, null, "date-time") }}
                  </div>
                </div>
              </v-list-item-subtitle>
              <v-list-item-subtitle>
                {{ email.bodyPreview }}
              </v-list-item-subtitle>
            </v-list-item>
            <v-divider v-if="index < emails.length - 1" class="mx-n3" />
          </template>
        </v-list>
        <div v-else class="px-4 pt-3">No emails have been received yet.</div>
      </v-card>
    </template>
    <template #right>
      <v-scroll-y-transition mode="out-in">
        <div v-if="loadingEmail" class="px-4 pt-3">
          <v-skeleton-loader type="card" />
        </div>
        <div
          v-else-if="activeEmail && activeEmailId"
          :key="activeEmailId"
          :loading="loading"
        >
          <app-button
            v-if="smAndDown"
            variant="outlined"
            color="accent"
            class="text-none mb-3"
            text="View Emails"
            :prepend-icon="mdiArrowLeft"
            @click="menuTab = 0"
          />
          <v-card elevation="0">
            <v-card-title data-testid="email-title">
              {{ activeEmail.subject }}
            </v-card-title>
            <v-card-subtitle data-testid="email-to" class="text-subtitle-2">
              To: {{ listToSentence(activeEmail.to) }}
            </v-card-subtitle>
            <v-card-subtitle data-testid="email-from" class="text-subtitle-2">
              From: {{ listToSentence(activeEmail.from) }}
            </v-card-subtitle>
            <v-card-subtitle
              v-if="activeEmail.attachments.length"
              class="d-flex ga-1 pt-1 flex-wrap"
            >
              <v-card
                v-for="attachment in activeEmail.attachments"
                :key="attachment.downloadUrl"
                elevation="0"
                data-testid="email-attachment"
                class="rounded border-thin py-1 px-2 text-body-2 d-flex align-center"
                :href="attachment.downloadUrl"
                target="_blank"
              >
                <div style="width: 100px">
                  <div class="text-truncate">
                    {{ attachment.filename }}
                  </div>
                  <div class="text-truncate">
                    {{ formatBytes(attachment.size) }}
                  </div>
                </div>
                <v-icon :icon="mdiDownload" class="ml-3 inherit-color" />
              </v-card>
            </v-card-subtitle>
            <v-card-text data-testid="email-body" class="wrap-pre">
              <template-renderer
                v-if="activeEmail.isHtml"
                class="frame"
                :model-value="activeEmail.body"
              />
              <template v-else>
                {{ activeEmail.body }}
              </template>
            </v-card-text>
          </v-card>
        </div>
        <div
          v-else
          class="h-100 w-100 justify-center align-center d-flex bg-surface rounded"
        >
          <v-card key="no-active-task" flat>
            <v-col align="center" justify="center" class="h-100 text-center">
              <v-card-title class="pt-0"> No Email Selected </v-card-title>
              <v-card-subtitle>
                Use the menu on the left to select an email.
              </v-card-subtitle>
            </v-col>
          </v-card>
        </div>
      </v-scroll-y-transition>
    </template>
  </side-by-side-tab-controller>
</template>
<script setup>
import TemplateRenderer from "@/components/shared/TemplateRenderer.vue";
import SideBySideTabController from "@/components/shared/SideBySideTabController.vue";

import { ref, toRef, watch } from "vue";
import {
  formatBytes,
  listToSentence,
  parseErrorMessage,
  timestampFormatter
} from "@/util/helpers";
import {
  getEAppInboundEmail,
  getEAppInboundEmails
} from "@/api/electronic-applications.service";
import { getCaseInboundEmail, getCaseInboundEmails } from "@/api/cases.service";
import { mdiArrowLeft, mdiDownload } from "@mdi/js";
import { useSnackbarStore } from "@/stores/snackbar";
import { useDisplay } from "vuetify/lib/framework.mjs";
import { useRouter } from "vue-router";

const props = defineProps({
  type: {
    type: String,
    validator: v => ["eapp", "case"].includes(v),
    required: true
  },
  id: {
    type: Number,
    required: true
  },
  emailId: {
    type: Number,
    required: false,
    default: null
  },
  active: Boolean
});

const pageIsActive = toRef(props, "active");

const router = useRouter();
const { mdAndUp, smAndDown } = useDisplay();

const menuTab = ref(props.emailId ? 1 : 0);
const activeEmailId = ref(props.emailId);
const activeEmail = ref(null);
const loadingEmails = ref(false);
const loadingEmail = ref(false);

const snackbar = useSnackbarStore();

let fetchAll;
let fetchOne;
if (props.type === "eapp") {
  fetchAll = getEAppInboundEmails;
  fetchOne = getEAppInboundEmail;
} else if (props.type === "case") {
  fetchAll = getCaseInboundEmails;
  fetchOne = getCaseInboundEmail;
} else throw `Unsupported Type ${props.type}`;

const emails = ref([]);

async function getEmails() {
  try {
    loadingEmails.value = true;
    emails.value = await fetchAll(props.id);
    emails.value.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  } catch (e) {
    snackbar.showErrorSnackbar({ message: parseErrorMessage(e) });
  } finally {
    loadingEmails.value = false;
  }
}
function viewEmail(emailId) {
  activeEmailId.value = emailId;
  menuTab.value = 1;
}

async function getEmail() {
  try {
    loadingEmail.value = true;
    activeEmail.value = await fetchOne(props.id, activeEmailId.value);
  } catch (e) {
    snackbar.showErrorSnackbar({ message: parseErrorMessage(e) });
  } finally {
    loadingEmail.value = false;
  }
}

function syncToQuery() {
  if (!pageIsActive.value) return;
  if (!activeEmail.value?.id) return;
  router.replace({
    query: {
      ...router.currentRoute.value.query,
      "email-id": activeEmailId.value
    }
  });
}

watch(
  activeEmailId,
  v => {
    if (!v) return;
    getEmail();
  },
  { immediate: true }
);

watch([activeEmail, pageIsActive], syncToQuery);

getEmails();
</script>
